// Copyright (c) Docugami, Inc. All rights reserved.
// https://github.com/fabsrc/es6-ukkonen-suffix-tree

// A directed edge between nodes in the form [EndNode, startIndex, endIndex]
export type TransitionEdge = [Node, number, number];

export class Node {
    transition: { [key: string]: TransitionEdge } = {};
    suffixLink: Node | null = null;
    suffixIndex: number = -1;

    constructor(index: number = -1) {
        this.suffixIndex = index;
    }

    addTransition(node: Node, [start, end]: [number, number], t: string): void {
        this.transition[t] = [node, start, end];
    }

    isLeaf(): boolean {
        return Object.keys(this.transition).length === 0;
    }
}

export class SuffixTree {
    k: number;
    i: number;
    s: Node;
    root: Node;
    bottom: Node;
    text: string;

    constructor() {
        this.text = '';

        this.root = new Node();
        this.bottom = new Node();
        this.root.suffixLink = this.bottom;

        this.s = this.root;
        this.k = 0;
        this.i = -1;
    }

    addString(str: string): SuffixTree {
        const temp = this.text.length;
        this.text += (temp) ? '⚇' + str : str;

        let [s, k, i] = [this.s, this.k, this.i];

        for (var j = temp; j < this.text.length; j++) {
            this.bottom.addTransition(this.root, [j, j], this.text[j]);
        }

        while (this.text[i + 1]) {
            i++;
            [s, k] = this.update(s, [k, i]);
            [s, k] = this.canonize(s, [k, i]);
        }

        [this.s, this.k, this.i] = [s, k, i];

        this.setSuffixIndices(this.root, 0);

        return this;
    }

    private setSuffixIndices(node: Node, height: number) {
        if (node.isLeaf()) {
            node.suffixIndex = this.text.length - height;
        }
        else {
            for (const [childNode, s, e] of Object.values(node.transition)) {
                // The suffix tree uses Infinity to represent the end of string so
                const edgeLength = this.getEdgeLength(s, e);
                this.setSuffixIndices(childNode, height + edgeLength);
            }
        }
    }

    private update(s: Node, [k, i]: [number, number]): [Node, number] {
        var oldr = this.root;
        var [endPoint, r] = this.testAndSplit(s, [k, i - 1], this.text[i]);

        while (!endPoint) {
            r.addTransition(new Node(), [i, Infinity], this.text[i]);

            if (oldr !== this.root) {
                oldr.suffixLink = r;
            }

            oldr = r;
            [s, k] = this.canonize(s.suffixLink!, [k, i - 1]);
            [endPoint, r] = this.testAndSplit(s, [k, i - 1], this.text[i]);
        }

        if (oldr !== this.root) {
            oldr.suffixLink = s;
        }

        return [s, k];
    }

    private testAndSplit(s: Node, [k, p]: [number, number], t: string): [boolean, Node] {
        if (k <= p) {
            var [s2, k2, p2] = s.transition[this.text[k]];

            if (t === this.text[k2 + p - k + 1]) {
                return [true, s];
            } else {
                var r = new Node();
                s.addTransition(r, [k2, k2 + p - k], this.text[k2]);
                r.addTransition(s2, [k2 + p - k + 1, p2], this.text[k2 + p - k + 1]);
                return [false, r];
            }
        } else {
            if (!s.transition[t])
                return [false, s];
            else
                return [true, s];
        }
    }

    private canonize(s: Node, [k, p]: [number, number]): [Node, number] {
        if (p < k)
            return [s, k];
        else {
            var [s2, k2, p2] = s.transition[this.text[k]];

            while (p2 - k2 <= p - k) {
                k = k + p2 - k2 + 1;
                s = s2;

                if (k <= p) {
                    [s2, k2, p2] = s.transition[this.text[k]];
                }
            }

            return [s, k];
        }
    }

    findLongestRepeatedSubstrings(n = 3) {
        var [text, root] = [this.text, this.root];
        var longestSubstrings = [];

        (function traverse(node, curStr = '') {
            if (node.isLeaf()) return;

            for (var t in node.transition) {
                var [s, a, b] = node.transition[t];
                if (!s.isLeaf()) {
                    var curCurStr = curStr;
                    var curSubStr = text.substring(a, b + 1);
                    curCurStr = (node === root) ? curSubStr : curCurStr + curSubStr;

                    longestSubstrings.push(curCurStr);
                    traverse(s, curCurStr);
                }
            }
        })(root);

        return longestSubstrings.sort((a, b) => b.length - a.length).slice(0, n);
    }

    toString() {
        var text = this.text;

        function traverse(node: Node, offset = '', ret = ''): string {
            for (var t in node.transition) {
                var [s, a, b] = node.transition[t];
                ret += offset + '["' + text.substring(a, b + 1) + '", ' + a + ', ' + b + ']\r\n';
                ret += traverse(s, offset + '\t');
            }
            return ret;
        }
        var res = traverse(this.root);
        return res;
    }

    print() {
        console.log(this.toString());
    }

    getEdgeLength(start: number, end: number) {
        const clampedEnd = Math.min(end, this.text.length - 1);
        return clampedEnd - start + 1;
    }
}
